import React, { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import Global from '../../../../store/global-selectors';
import { Button } from 'ui-components/components/button';
import { Tooltip } from 'ui-components/components/tooltip';
import { TooltipContent } from 'ui-components/components/tooltip/tooltip-content';
import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { Input } from 'ui-components/components/input';
import { MessageDescriptorPropType } from '../../../../prop-types/intl';
import messages from '../messages';

import style from './style.module.less';

const WebHookForm = ({
  setName,
  name = '',
  setUrl,
  url = '',
  onSave,
  onCancel,
  selectedStore,
  setSelectedStore,
  hookType,
  setHookType,
  webHookDropdown,
  isSaveClicked = false,
  validateUrl,
}) => {
  const intl = useIntl();
  const activeStores = useSelector(Global.selectSellerActiveStores);
  const selectedStoreName =
    activeStores.find((store) => store.name === selectedStore?.name)?.name || intl.formatMessage(messages.allStores);

  const handleSetHookType = useCallback((option) => setHookType(option), [setHookType]);
  const handleSetSelectedStore = useCallback((store) => setSelectedStore(store), [setSelectedStore]);

  return (
    <div className={style['webhook-form']}>
      <div className={style['webhook-form-row1']}>
        <Tooltip shouldHideTooltip={name.length === 0 && !isSaveClicked}>
          <div>
            <Input
              className={style['webhook-input']}
              hasError={name.length === 0 && isSaveClicked}
              onChange={setName}
              placeholder={intl.formatMessage(messages.nameDescription)}
              value={name}
            />
          </div>
          <TooltipContent>
            <FormattedMessage {...messages.cannotBeEmpty} />
          </TooltipContent>
        </Tooltip>
        <DropdownMenu className={style['webhook-input']} dropdownMenuShouldHaveWidthOfButton>
          <DropdownMenuToggler className={style['product-type-dropdown-selector']} theme='white'>
            <FormattedMessage {...hookType.messageDescriptor} />
          </DropdownMenuToggler>
          {webHookDropdown.map((option) => (
            <DropdownMenuItem key={option.value} onClick={() => handleSetHookType(option)} selected={hookType.value === option.value}>
              <FormattedMessage {...option.messageDescriptor} />
            </DropdownMenuItem>
          ))}
        </DropdownMenu>
        <DropdownMenu className={style['webhook-input']} dropdownMenuShouldHaveWidthOfButton>
          <DropdownMenuToggler className={style['product-type-dropdown-selector']} required={isSaveClicked} theme='white'>
            {selectedStoreName}
          </DropdownMenuToggler>
          {activeStores &&
            activeStores.map((store) => (
              <DropdownMenuItem key={store.name} onClick={() => handleSetSelectedStore(store)} selected={selectedStoreName === store.name}>
                {store.name}
              </DropdownMenuItem>
            ))}
        </DropdownMenu>
      </div>
      <div className={style['webhook-form-row2']}>
        <Tooltip shouldHideTooltip={(url.length === 0 || !validateUrl(url)) && !isSaveClicked}>
          <div>
            <Input
              className={style['webhook-input-url']}
              hasError={(url.length === 0 || !validateUrl(url)) && isSaveClicked}
              onChange={setUrl}
              placeholder={intl.formatMessage(messages.sendToURL)}
              value={url}
            />
          </div>
          <TooltipContent>
            <FormattedMessage {...messages.mustBeValidUrl} />
          </TooltipContent>
        </Tooltip>
        <Button onClick={onSave} theme='primary'>
          <FormattedMessage {...messages.saveButton} />
        </Button>
        <Button className={style['webhook-cancel-button']} onClick={onCancel} theme='secondary'>
          <FormattedMessage {...messages.cancelButton} />
        </Button>
      </div>
      <div className={style['border-line']} />
    </div>
  );
};

WebHookForm.propTypes = {
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  setName: PropTypes.func.isRequired,
  setUrl: PropTypes.func.isRequired,
  name: PropTypes.string,
  url: PropTypes.string,
  activeStores: PropTypes.object,
  selectedStore: PropTypes.object,
  setSelectedStore: PropTypes.func.isRequired,
  hookType: PropTypes.shape({
    messageDescriptor: MessageDescriptorPropType,
    value: PropTypes.string,
  }).isRequired,
  setHookType: PropTypes.func.isRequired,
  webHookDropdown: PropTypes.array.isRequired,
  isSaveClicked: PropTypes.bool,
  validateUrl: PropTypes.func.isRequired,
};

export default WebHookForm;
